/**
 * 帳號同步協調器 —— 把「登入狀態」接到「雲端同步」與「progress mirror」。
 *
 * 啟動時呼一次 `initAccountSync()`(main.ts,在 ensureFamilyState 之後):
 *   - 訂閱 `onAuthChange`。
 *   - 登入時:
 *       · 若雲端已有列 → `pullCloudIntoLocal()`(用雲端覆蓋本機,跨裝置一致)。
 *       · 若雲端 0 列但本機有資料 → `pushAllLocalToCloud()`(首次登入遷移)。
 *       · 註冊 `setProgressMirror(id => pushProfileToCloud(id))`,之後每次
 *         saveProgress 都自動上傳。
 *       · 廣播 `ef:account-changed` 讓 UI(設定頁)重繪。
 *   - 登出時:取消 mirror + 廣播。
 *
 * 鐵則:`isCloudEnabled()` 為 false(沒設 env)→ initAccountSync 整個 no-op,
 * 行為跟現在 100% 一樣(純本機 profile)。所有非同步流程吞錯不 throw。
 */
import { isCloudEnabled, onAuthChange } from './supabase';
import { setProgressMirror } from './progress';
import { pushProfileToCloud, mergeCloudOnSignIn, purgeJunkProfiles } from './cloud-sync';

/** 已初始化旗標 —— initAccountSync 冪等,重複呼叫只掛一次 listener。 */
let _initialized = false;
let _unsubscribe: (() => void) | null = null;

/** UI 可監聽此事件在登入/登出時重繪帳號區。 */
export const ACCOUNT_CHANGED_EVENT = 'ef:account-changed';

function emitAccountChanged(loggedIn: boolean): void {
  try {
    window.dispatchEvent(
      new CustomEvent(ACCOUNT_CHANGED_EVENT, { detail: { loggedIn } }),
    );
  } catch {
    /* 測試環境 / 無 window —— 略過 */
  }
}

/**
 * 登入後的一次性協調:合併本機與雲端(不覆蓋、不丟資料),然後掛上 mirror。
 * 任何錯誤吞掉(降級純本機)。
 */
async function onSignedIn(): Promise<void> {
  try {
    // 登入時合併:雲端空→遷移本機;雲端有料→pull + 聯集本機獨有 + 衝突另存,兩份都留。
    await mergeCloudOnSignIn();
    // 之後每次 saveProgress → 自動上傳該 profile。
    setProgressMirror((id) => {
      void pushProfileToCloud(id);
    });
  } catch {
    /* 同步失敗不阻斷 —— 仍可純本機遊玩 */
  } finally {
    emitAccountChanged(true);
  }
}

/** 登出:取消 mirror(本機照常運作,只是不再上傳)。 */
function onSignedOut(): void {
  setProgressMirror(undefined);
  emitAccountChanged(false);
}

/**
 * 啟動帳號同步。雲端未設定 → 整個 no-op(向後相容底線)。冪等。
 * @returns 取消訂閱函式(雲端未啟用時為 no-op)。
 */
export function initAccountSync(): () => void {
  if (!isCloudEnabled()) return () => {};
  if (_initialized) return _unsubscribe ?? (() => {});
  _initialized = true;

  // 開機先清掉「合併 bug」留下的「(本機)」垃圾 profile(自我修復,不靠使用者清快取)。
  purgeJunkProfiles();

  _unsubscribe = onAuthChange((user) => {
    if (user) {
      void onSignedIn();
    } else {
      onSignedOut();
    }
  });

  return () => {
    _unsubscribe?.();
    _unsubscribe = null;
    _initialized = false;
    setProgressMirror(undefined);
  };
}

/** 測試用:重設模組內部狀態(讓每個 test case 從乾淨起點開始)。 */
export function _resetAccountSyncForTests(): void {
  _unsubscribe?.();
  _unsubscribe = null;
  _initialized = false;
  setProgressMirror(undefined);
}
